import { takeLatest, call, put } from 'redux-saga/effects';

import api from '../api';
import { Employee } from './actions';
import {
    listEmployeesRequest,
    listEmployeesSuccess,
    listEmployeesFailure,
    updateEmployeesRequest,
    updateEmployeesSuccess,
    updateEmployeesFailure,
    getEmployeeRequest,
    getEmployeeSuccess,
    getEmployeeFailure,
    updateEmployeeRequest,
    createEmployeeRequest,
} from './actions';

// API calls
const listEmployeeApi = async (filters: string, members_fields: string, excludes: string): Promise<Employee[]> => {
    let params = new URLSearchParams({
        filters: filters || '',
        excludes: excludes || '',
        member_fields: members_fields
    }).toString();

    const response = await api.get('/api/members/list/?' + params);
    return response.data;
};

const updateEmployeeApi = async (employee: Employee[], update_fields: string[], members_fields?: string): Promise<Employee[]> => {
    let params = "";
    if (members_fields) {
        params = "?"+ new URLSearchParams({
            member_fields: members_fields
        }).toString();
    };

    const data = Object.values(employee).map((el) => Object.fromEntries(Object.entries(el).filter(([key]) => update_fields.includes(key))));

    const response = await api.post('/api/members/post/' + params, data);
    return response.data;
};

const createEmployeeApi = async (employee: Employee, update_fields: string[], members_fields?: string): Promise<Employee[]> => {
    let params = "";
    if (members_fields) {
        params = "?"+ new URLSearchParams({
            member_fields: members_fields
        }).toString();
    }

    const data = Object.fromEntries(Object.entries(employee).filter(([key]) => update_fields.includes(key)));

    /*
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
        if (data[key] instanceof FileList) {
            Array.from(data[key]).forEach((file) => {
                formData.append(key, file);
            });
        } else {
            formData.append(key, data[key] ? data[key].toString() : '');
        }
    });
    */

    const response = await api.post("/api/members/post/" + params, data, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return response.data;
};


// Sagas
function* handleListEmployee(action: ReturnType<typeof listEmployeesRequest>) {
    try {
        const data: Employee[] = yield call(
            listEmployeeApi,
            action.payload.filters,
            action.payload.members_fields,
            action.payload.excludes
        );
        yield put(listEmployeesSuccess({data: data, save_as: action.payload.save_as || 'employees' }));
    } catch (error: any) {
        yield put(listEmployeesFailure(error.response?.data || 'Failed to list employees'));
    }
}

function* handleUpdateEmployees(action: ReturnType<typeof updateEmployeesRequest>) {
    try {
        const data: Employee[] = yield call(
            updateEmployeeApi,
            action.payload.employees,
            action.payload.update_fields,
            action.payload.members_fields,
        );
        yield put(updateEmployeesSuccess(data));
    } catch (error: any) {
        yield put(updateEmployeesFailure(error.response?.data || 'Failed to update employees'));
    }
}

function* handleGetEmployee(action: ReturnType<typeof getEmployeeRequest>) {
    try {
        const filters = 'id:' + action.payload.id;
        const data: Employee[] = yield call(
            listEmployeeApi,
            filters,
            action.payload.members_fields,
            ''
        );
        yield put(getEmployeeSuccess(data.length ? data[0] : {id: -1}));
    } catch (error: any) {
        yield put(getEmployeeFailure(error.response?.data || 'Failed to get employee'));
    }
}

function* handleUpdateEmployee(action: ReturnType<typeof updateEmployeeRequest>) {
    try {
        const data: Employee[] = yield call(
            updateEmployeeApi,
            [action.payload.employee],
            action.payload.update_fields,
            action.payload.members_fields,
        );
        yield put(updateEmployeesSuccess(data));
    } catch (error: any) {
        yield put(updateEmployeesFailure(error.response?.data || 'Failed to update employees'));
    }
}

function* handleCreateEmployee(action: ReturnType<typeof createEmployeeRequest>) {
    try {
        const data: Employee[] = yield call(
            createEmployeeApi,
            action.payload.employee,
            action.payload.update_fields,
            action.payload.members_fields
        );
        yield put(updateEmployeesSuccess(data));
    } catch (error: any) {
        yield put(updateEmployeesFailure(error.response?.data || 'Failed to create employee'));
    }
}

// Watcher sagas
export function* watchEmployeeSaga() {
    yield takeLatest(listEmployeesRequest.type, handleListEmployee); // Listen for list employees request
    yield takeLatest(updateEmployeesRequest.type, handleUpdateEmployees);
    yield takeLatest(getEmployeeRequest.type, handleGetEmployee);
    yield takeLatest(updateEmployeeRequest.type, handleUpdateEmployee);
    yield takeLatest(createEmployeeRequest.type, handleCreateEmployee);
}
